import React from 'react';
import {
    Button,
    Container,
    Icon,
    Image,
    Link,
    LinkBox,
    LinkOverlay,
    SimpleGrid,
    Text,
    List,
    VStack
} from '@chakra-ui/react';
import { SquareArrowOutUpRight } from 'lucide-react';
import HomeLayout from '../../components/layouts/homelayout';
import PageHeader from '../../components/sections/pageheader';
import TextWithLeftMedia from '../../components/sections/textwithleftmedia';
import TextWithRightMedia from '../../components/sections/textwithrightmedia';

import { CSCPolicies, PosterItems, PSListItems } from '../../components/ui/corporateresponsibility/responsiblepractices/lists';
import HeroWithTopMedia from '../../components/sections/herowithtopmedia';

import MHW from '../../components/ui/corporateresponsibility/responsiblepractices/MHW.jpg';
import ODIPhoto from '../../components/ui/corporateresponsibility/responsiblepractices/ODIphoto.jpg';
import OrganizationImages from '../../components/ui/corporateresponsibility/responsiblepractices/OrganizationImages';
import SorelSandals from '../../components/ui/corporateresponsibility/responsiblepractices/SorelRedSandals.jpg';
import elevatingResponsibility from '../../components/ui/corporateresponsibility/responsiblepractices/elevating-responsibility.jpg';
import leveragingInnovation from '../../components/ui/corporateresponsibility/responsiblepractices/leveraging-innovation.jpg';
import scTransparency from '../../components/ui/corporateresponsibility/responsiblepractices/supply-chain-transparency.png';

import Sorel from '../../components/ui/corporateresponsibility/highlights/Sorel.jpg';


export default function ResponsiblePractices() {
    return (
        
        <HomeLayout>
            <title>Corporate Responsibility - Responsible Practices</title>
            <meta name="description" content="Key practices that elevate Corporate Responsibility." />
            <PageHeader 
                heading='Responsible Practices'
                bkgImg={Sorel}
            />
            <Container maxW={'6xl'}>
                <SimpleGrid align={'center'} columns={{ base: 1, md: 2, lg: 3 }} gap={12} mb={16}>
                    <HeroWithTopMedia
                        title={'Supply Chain Transparency Map'}
                        subTitle={'Our transparency map provides visibility to where products made by Columbia Sportswear Company brands are produced. We utilize the OSH tool to enable us to be transparent to stakeholders.'}
                        cta={
                            <LinkBox as={'a'} mt={'auto'}>
                                <LinkOverlay
                                    href='/corporate-responsibility-group/responsible-practices/supply-chain'
                                    >
                                        <Button
                                            size="md"
                                            p={6}
                                            bg="black"
                                            color="white"
                                            borderRadius={8}
                                            >
                                            LEARN MORE
                                            <Icon
                                                color="white"
                                                w={5}
                                                h={5}
                                                as={SquareArrowOutUpRight}
                                                pl="4px"
                                                ml={1}
                                            >
                                            </Icon>
                                        </Button>
                                </LinkOverlay>
                            </LinkBox>
                        }
                        media={
                        <Image width="100%" borderRadius={10} src={scTransparency} />
                        }
                    ></HeroWithTopMedia>
                    <HeroWithTopMedia
                        title="Leveraging Innovation & Design to Create Social & Environmental Value"
                        subTitle={'Driving our core competencies of innovation and design across our portfolio of brands to create social and environmental value. In doing this, we aim to increase consumer loyalty and develop a strong connection to our brands.'}
                        cta={        
                            <LinkBox as={'a'} mt={'auto'}>
                                <LinkOverlay
                                    href='/corporate-responsibility-group/responsible-practices/leveraging-innovation'
                                    >
                                        <Button
                                            size="md"
                                            p={6}
                                            bg="black"
                                            color="white"
                                            borderRadius={8}
                                            >
                                            LEARN MORE
                                            <Icon
                                                color="white"
                                                w={5}
                                                h={5}
                                                as={SquareArrowOutUpRight}
                                                pl="4px"
                                                ml={1}
                                            >
                                            </Icon>
                                        </Button>
                                </LinkOverlay>
                            </LinkBox>
                        }
                        media={
                        <Image
                            borderRadius={10}
                            src={leveragingInnovation}
                        />
                        }
                    ></HeroWithTopMedia>
                    <HeroWithTopMedia
                        title="Elevating Responsibility, Integrity and Compliance"
                        subTitle={'Promoting responsible manufacturing by selecting vendors that have demonstrated commitments to continuously improve their Social Responsibility, Environmental and Product Compliance metrics.'}
                        cta={
                            <LinkBox as={'a'} mt={'auto'}>
                                <LinkOverlay
                                    href='/corporate-responsibility-group/responsible-practices/elevating-responsibility'
                                    >
                                        <Button
                                            size="md"
                                            p={6}
                                            bg="black"
                                            color="white"
                                            borderRadius={8}
                                            >
                                            LEARN MORE
                                            <Icon
                                                color="white"
                                                w={5}
                                                h={5}
                                                as={SquareArrowOutUpRight}
                                                pl="4px"
                                                ml={1}
                                            >
                                            </Icon>
                                        </Button>
                                </LinkOverlay>
                            </LinkBox>
                        }
                        media={<Image borderRadius={10} src={elevatingResponsibility} />}
                    ></HeroWithTopMedia>
                </SimpleGrid>
            </Container>
            
            <Container maxW={'6xl'}>
                <h2>Collaboration and Engagement</h2>
                <p>
                We recognize that we are a single player in a large, complex, global supply chain. To impact meaningful change, we need
                to work with brand, industry, and multi-stakeholder partners to:
                </p>
                    
                <List.Root pl={4} my={5} gap={2}>
                    <List.Item>
                        Align on common industry standards for evaluating our business and our supply chain. Standardization will 
                        enable greater understanding of social and environmental impacts as well as increased efficiency in evaluation 
                        methods and broader comparability of results. Industry standards allow us to get on the same page about 
                        possible impacts and support our collaborative work towards shared goals around social responsibility, 
                        environmental sustainability and product compliance.
                    </List.Item>
                    <List.Item>
                        Develop shared tools to reduce costs and improve implementation of social, environmental, and compliance programs
                    </List.Item>
                    <List.Item>
                        Share resources with industry partners on specific projects to increase collective impact.
                    </List.Item>
                </List.Root>

                <Text>
                    We select organizations for investment and engagement based on their ability to influence systemic change and their 
                    relevance to our business and supply chain. We also seek to work directly with other brands and retailers who share 
                    our values and whose products are made in the same factories as ours. Click on the organization below to learn more.
                </Text>
                <SimpleGrid p={20} columns={{ base: 1, md: 3 }} columnGap={'var(--space-m)'} rowGap={'var(--space-xl)'} align="center">
                    {OrganizationImages.map((image, index) => (
                        <Link key={index} href={image.href} alignSelf={'center'} target="_blank" rel="noopener noreferrer">
                            <Image src={image.src} width="200px" p={2}/>
                        </Link>
                    ))}
                </SimpleGrid>
            </Container>
            <TextWithLeftMedia media={
                    <>
                        <VStack gap={2} alignItems="start" >
                            <Image src={ODIPhoto} /> 
                            <Text>Photo Credit: Outdoor Industry Association </Text>
                        </VStack>
                    </>
                }
                title={<h3>Position Statements</h3>}
                content={
                    <>
                        We value ethical practices at every step in the supply chain and work with industry groups, civil society organizations and government organizations to keep educated on ethical standards and practices across the supply chain. Read more about Columbia’s specific positions on:
                        <List.Root pl={4} mt={4} gap={2}>
                            {PSListItems.map((item, index) => (
                                <List.Item key={index}>
                                    <Link href={item.link} target="_blank" rel="noopener noreferrer"> 
                                        <Text textDecoration={"underline"}>{item.title}</Text>
                                    </Link>
                                </List.Item>
                            ))}
                        </List.Root>
                    </>
                }/>
            <TextWithRightMedia media={<Image src={SorelSandals} />}
                title={<h3>Product Compliance Standards, Policies, and Manuals</h3>}
                content={
                    <>
                        <List.Root pl={4} mt={4} gap={2}>
                            {CSCPolicies.map((item, index) => (
                                <List.Item key={index}>
                                    <Link href={item.link} target="_blank" rel="noopener noreferrer"> 
                                        <Text textDecoration={"underline"}>{item.policyName}</Text>
                                    </Link>
                                </List.Item>
                            ))}
                        </List.Root>
                    </>
                }/>
            <TextWithLeftMedia media={<Image src={MHW} />}
                title={<h3>Social Responsibility Standards, Policies, and Manuals</h3>}
                content={
                    <>
                        <Text> Our Standards of Manufacturing Practices (SMP), a.k.a. Supplier Code of Conduct, is at the center of our Social Responsibility Program in our supply chain. </Text>
                        <List.Root pl={4} mt={4} gap={2}>
                            {PosterItems.map((item, index) => (
                                <List.Item key={index}>
                                    <Link href={item.link} target="_blank" rel="noopener noreferrer"> 
                                        <Text textDecoration={'underline'}>{item.poster}</Text>
                                    </Link>
                                </List.Item>
                            ))}
                        </List.Root>
                    </>
                }/>
        </HomeLayout>
    );
};